import React, { useState, useEffect, useCallback, useRef } from 'react';

const slides = [
  {
    id: 'dashboard',
    tag: 'AI Projections',
    title: 'Weekly Player\nProjections',
    description: 'Our XGBoost + LightGBM ensemble model analyzes historical stats, matchup data, Vegas lines, and injury reports to project every player\'s fantasy output before the week begins.',
    bullets: [
      'Projections for all 6 positions: QB, RB, WR, TE, K, DST',
      'Confidence intervals on every prediction',
      'Injury risk badges pulled live from Sleeper',
      'Favorite players pinned to the top',
    ],
    accent: '#4d7cff',
    preview: <DashboardPreview />,
  },
  {
    id: 'analytics',
    tag: 'Season Stats',
    title: 'Full Season\nLeaderboards',
    description: 'Explore real game-by-game results going back to 2015. Filter by position, search any player, and see how their season stacks up across every stat that matters.',
    bullets: [
      'Fantasy leaderboards for every season 2015–2025',
      'Search any player across all positions',
      'Week-by-week breakdown with trend charts',
      'Compare season averages side by side',
    ],
    accent: '#34d399',
    preview: <AnalyticsPreview />,
  },
  {
    id: 'compare',
    tag: 'Head to Head',
    title: 'Compare Any\nTwo Players',
    description: 'Stack two players against each other with a full visual breakdown — fantasy points, yards, touchdowns, and a week-by-week chart showing who\'s been more consistent.',
    bullets: [
      'Search and compare any two players instantly',
      'Side-by-side stat cards with visual indicators',
      'Overlapping trend chart across shared seasons',
      'Great for start/sit decisions',
    ],
    accent: '#f59e0b',
    preview: <ComparePreview />,
  },
  {
    id: 'accuracy',
    tag: 'Model Performance',
    title: 'Transparent\nModel Accuracy',
    description: 'We publish our model\'s mean absolute error every week so you always know how much to trust the projections. No black boxes — just honest, measurable performance.',
    bullets: [
      'Per-position MAE tracked every season',
      'Holdout test set validation (no data leakage)',
      'RB MAE as low as 5.09 pts · WR 5.10 pts',
      'Model improves as more season data comes in',
    ],
    accent: '#a78bfa',
    preview: <AccuracyPreview />,
  },
];

// ── Slide Previews ────────────────────────────────────────────────────────────

function DashboardPreview() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1200);
    return () => clearInterval(id);
  }, []);

  const players = [
    { name: 'Josh Allen',      pos: 'QB', team: 'BUF', pts: 28.4, bar: 92 },
    { name: 'Saquon Barkley',  pos: 'RB', team: 'PHI', pts: 22.1, bar: 78 },
    { name: 'Ja\'Marr Chase',  pos: 'WR', team: 'CIN', pts: 19.8, bar: 70 },
    { name: 'Sam LaPorta',     pos: 'TE', team: 'DET', pts: 14.2, bar: 52 },
    { name: 'Justin Tucker',   pos: 'K',  team: 'BAL', pts: 10.5, bar: 40 },
  ];

  const posColors = { QB: '#4d7cff', RB: '#34d399', WR: '#f59e0b', TE: '#a78bfa', K: '#f87171' };

  return (
    <div className="w-full h-full flex flex-col gap-2 p-1">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-bold text-white/60 uppercase tracking-widest">2026 · Week 1 Projections</div>
        <div className="flex gap-1">
          {['All','QB','RB','WR','TE'].map((p,i) => (
            <div key={p} className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${i===0 ? 'bg-[#4d7cff] text-white' : 'bg-white/5 text-white/30'}`}>{p}</div>
          ))}
        </div>
      </div>
      {/* Rows */}
      <div className="flex flex-col gap-1.5">
        {players.map((p, i) => (
          <div
            key={p.name}
            className="flex items-center gap-2 bg-white/[0.03] rounded-xl px-3 py-2 transition-all duration-500"
            style={{ opacity: tick % 5 === i ? 1 : 0.7, transform: tick % 5 === i ? 'scale(1.01)' : 'scale(1)' }}
          >
            <div className="text-[9px] text-white/20 w-3 font-mono">{i+1}</div>
            <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center text-[8px] font-bold" style={{ color: posColors[p.pos] }}>{p.pos[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold text-white truncate">{p.name}</div>
              <div className="text-[8px] text-white/30">{p.team}</div>
            </div>
            {/* bar */}
            <div className="w-16 h-1 bg-white/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${p.bar}%`, backgroundColor: posColors[p.pos], opacity: 0.7 }}
              />
            </div>
            <div className="text-[11px] font-bold text-white/80 w-8 text-right">{p.pts}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsPreview() {
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setAnimPct(p => p >= 100 ? 0 : p + 2), 40);
    return () => clearInterval(id);
  }, []);

  const weeks = [8,14,22,18,26,19,24,28,16,22,25,19,27,23,18,24,21,26];
  const maxPt = Math.max(...weeks);
  const chartH = 60;

  const points = weeks.map((v, i) => {
    const x = (i / (weeks.length - 1)) * 100;
    const y = chartH - (v / maxPt) * chartH;
    return `${x},${y}`;
  }).join(' ');

  const players = [
    { name: 'Josh Allen',     pts: 412.4, games: 17, pos: 'QB' },
    { name: 'Lamar Jackson',  pts: 389.1, games: 16, pos: 'QB' },
    { name: 'Saquon Barkley', pts: 361.8, games: 17, pos: 'RB' },
  ];

  return (
    <div className="w-full h-full flex flex-col gap-2 p-1">
      <div className="text-[10px] font-bold text-white/60 uppercase tracking-widest mb-1">2024 Season Leaderboard</div>
      {/* Mini table */}
      <div className="bg-white/[0.03] rounded-xl overflow-hidden mb-2">
        <div className="grid grid-cols-4 px-3 py-1.5 border-b border-white/[0.05]">
          {['#','Player','Games','Pts'].map(h => (
            <div key={h} className="text-[8px] text-white/30 uppercase font-bold">{h}</div>
          ))}
        </div>
        {players.map((p, i) => (
          <div key={p.name} className="grid grid-cols-4 px-3 py-2 border-b border-white/[0.03] last:border-0">
            <div className="text-[9px] text-white/30">{i+1}</div>
            <div>
              <div className="text-[9px] font-semibold text-white">{p.name}</div>
              <div className="text-[7px] text-white/30">{p.pos}</div>
            </div>
            <div className="text-[9px] text-white/50">{p.games}</div>
            <div className="text-[10px] font-bold text-[#34d399]">{p.pts}</div>
          </div>
        ))}
      </div>
      {/* Sparkline */}
      <div className="bg-white/[0.03] rounded-xl p-3 flex-1">
        <div className="text-[8px] text-white/30 mb-2">Josh Allen — Weekly Fantasy Pts</div>
        <svg viewBox={`0 0 100 ${chartH}`} className="w-full" preserveAspectRatio="none" style={{height: 50}}>
          <defs>
            <linearGradient id="lgA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#34d399" stopOpacity="0"/>
            </linearGradient>
            <clipPath id="revealA">
              <rect x="0" y="0" width={animPct} height={chartH}/>
            </clipPath>
          </defs>
          <polyline points={points} fill="none" stroke="#34d399" strokeWidth="1.5" clipPath="url(#revealA)" vectorEffect="non-scaling-stroke"/>
        </svg>
      </div>
    </div>
  );
}

function ComparePreview() {
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setAnimPct(p => p >= 100 ? 0 : p + 1.5), 40);
    return () => clearInterval(id);
  }, []);

  const p1Weeks = [18,24,32,14,28,22,36,19,26,30,18,24,28,22,26,30,24,28];
  const p2Weeks = [12,19,24,28,16,22,18,26,20,24,22,18,26,24,20,22,28,24];
  const maxPt = Math.max(...p1Weeks, ...p2Weeks);
  const chartH = 50;

  const toPoints = (arr) => arr.map((v, i) => {
    const x = (i / (arr.length - 1)) * 100;
    const y = chartH - (v / maxPt) * chartH;
    return `${x},${y}`;
  }).join(' ');

  const stats = [
    { label: 'Avg Pts',  v1: 24.8, v2: 21.4, unit: '' },
    { label: 'Rush Yds', v1: 62,   v2: 91,   unit: '' },
    { label: 'Pass Yds', v1: 284,  v2: 0,    unit: '' },
    { label: 'TDs',      v1: 2.1,  v2: 1.4,  unit: '' },
  ];

  return (
    <div className="w-full h-full flex flex-col gap-2 p-1">
      {/* Player headers */}
      <div className="grid grid-cols-2 gap-2 mb-1">
        {[
          { name: 'Josh Allen', team: 'BUF · QB', color: '#4d7cff' },
          { name: 'Lamar Jackson', team: 'BAL · QB', color: '#f59e0b' },
        ].map(p => (
          <div key={p.name} className="bg-white/[0.04] rounded-xl px-3 py-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex-shrink-0" style={{ background: `${p.color}30`, border: `1px solid ${p.color}50` }}/>
            <div>
              <div className="text-[9px] font-bold text-white">{p.name}</div>
              <div className="text-[7px] text-white/30">{p.team}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Stat bars */}
      <div className="flex flex-col gap-1.5">
        {stats.map(s => {
          const total = s.v1 + s.v2 || 1;
          return (
            <div key={s.label} className="flex items-center gap-2">
              <div className="text-[8px] text-white/30 w-12 text-right">{s.v1}</div>
              <div className="flex-1 flex gap-0.5 h-1.5 rounded-full overflow-hidden bg-white/[0.04]">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(s.v1/total)*100}%`, background: '#4d7cff' }}/>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(s.v2/total)*100}%`, background: '#f59e0b' }}/>
              </div>
              <div className="text-[8px] text-white/30 w-8">{s.v2}</div>
              <div className="text-[7px] text-white/20 w-10">{s.label}</div>
            </div>
          );
        })}
      </div>
      {/* Chart */}
      <div className="bg-white/[0.03] rounded-xl p-2 flex-1 mt-1">
        <svg viewBox={`0 0 100 ${chartH}`} className="w-full" preserveAspectRatio="none" style={{height:44}}>
          <defs>
            <clipPath id="revealC"><rect x="0" y="0" width={animPct} height={chartH}/></clipPath>
          </defs>
          <polyline points={toPoints(p1Weeks)} fill="none" stroke="#4d7cff" strokeWidth="1.5" clipPath="url(#revealC)" vectorEffect="non-scaling-stroke"/>
          <polyline points={toPoints(p2Weeks)} fill="none" stroke="#f59e0b" strokeWidth="1.5" clipPath="url(#revealC)" vectorEffect="non-scaling-stroke"/>
        </svg>
      </div>
    </div>
  );
}

function AccuracyPreview() {
  const [animIdx, setAnimIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setAnimIdx(i => (i + 1) % 6), 900);
    return () => clearInterval(id);
  }, []);

  const positions = [
    { pos: 'QB',  mae: 6.14, color: '#4d7cff',  pct: 62 },
    { pos: 'RB',  mae: 5.09, color: '#34d399',  pct: 74 },
    { pos: 'WR',  mae: 5.10, color: '#f59e0b',  pct: 73 },
    { pos: 'TE',  mae: 3.89, color: '#a78bfa',  pct: 88 },
    { pos: 'K',   mae: 3.60, color: '#f87171',  pct: 91 },
    { pos: 'DST', mae: 4.31, color: '#38bdf8',  pct: 83 },
  ];

  return (
    <div className="w-full h-full flex flex-col gap-2 p-1">
      <div className="text-[10px] font-bold text-white/60 uppercase tracking-widest mb-1">Model Accuracy — 2024 Season</div>
      <div className="flex flex-col gap-2">
        {positions.map((p, i) => (
          <div key={p.pos} className="flex items-center gap-3">
            <div className="text-[9px] font-bold w-6" style={{ color: p.color }}>{p.pos}</div>
            <div className="flex-1 h-2 bg-white/[0.05] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: animIdx >= i ? `${p.pct}%` : '0%',
                  backgroundColor: p.color,
                  opacity: 0.8,
                }}
              />
            </div>
            <div className="text-[10px] font-bold text-white/60 w-12 text-right">
              ±{p.mae} <span className="text-[8px] text-white/25">pts</span>
            </div>
          </div>
        ))}
      </div>
      {/* Summary card */}
      <div className="mt-2 bg-white/[0.03] rounded-xl px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[8px] text-white/30 uppercase tracking-widest">Best Accuracy</div>
          <div className="text-[13px] font-bold text-[#f87171]">Kicker</div>
        </div>
        <div className="text-right">
          <div className="text-[8px] text-white/30 uppercase tracking-widest">Avg MAE</div>
          <div className="text-[13px] font-bold text-white">4.69 <span className="text-[9px] text-white/30">pts</span></div>
        </div>
        <div className="text-right">
          <div className="text-[8px] text-white/30 uppercase tracking-widest">Seasons</div>
          <div className="text-[13px] font-bold text-[#4d7cff]">2015–25</div>
        </div>
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

const HowItWorksModal = ({ onClose }) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1); // 1=forward, -1=back
  const [animating, setAnimating] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Entrance animation
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const goTo = useCallback((idx) => {
    if (animating || idx === current) return;
    setDirection(idx > current ? 1 : -1);
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 280);
  }, [animating, current]);

  const next = () => goTo(Math.min(current + 1, slides.length - 1));
  const prev = () => goTo(Math.max(current - 1, 0));

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, animating]); // eslint-disable-line

  // Touch swipe
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    touchStart.current = null;
  };

  const slide = slides[current];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-2 sm:p-4 transition-all duration-300"
      style={{
        background: visible ? 'rgba(5,13,31,0.92)' : 'rgba(5,13,31,0)',
        backdropFilter: visible ? 'blur(12px)' : 'blur(0px)',
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="relative w-full max-w-5xl transition-all duration-300"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(16px)',
        }}
      >
        {/* Glow behind card */}
        <div
          className="absolute -inset-px rounded-[32px] blur-xl transition-colors duration-700"
          style={{ background: `${slide.accent}18` }}
        />

        <div className="relative bg-[#080f20] border border-white/[0.08] rounded-t-[28px] sm:rounded-[32px] overflow-hidden max-h-[92vh] flex flex-col"
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

          {/* Top bar */}
          <div className="flex items-center justify-between px-5 sm:px-8 pt-5 sm:pt-6 pb-0 flex-shrink-0">
            {/* Step dots */}
            <div className="flex items-center gap-2">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => goTo(i)}
                  className="transition-all duration-300 rounded-full"
                  style={{
                    width: i === current ? 24 : 6,
                    height: 6,
                    background: i === current ? slide.accent : 'rgba(255,255,255,0.12)',
                  }}
                />
              ))}
            </div>

            {/* Close */}
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors text-white/40 hover:text-white"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Main content */}
          <div className="grid lg:grid-cols-2 gap-0 overflow-y-auto flex-1">

            {/* Left — copy */}
            <div
              className="flex flex-col justify-center px-5 sm:px-8 py-5 sm:py-8 transition-all duration-280"
              style={{
                opacity: animating ? 0 : 1,
                transform: animating ? `translateX(${direction * -30}px)` : 'translateX(0)',
              }}
            >
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-5 w-fit"
                style={{ background: `${slide.accent}18`, color: slide.accent }}
              >
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: slide.accent }}/>
                {slide.tag}
              </div>

              <h2 className="text-2xl sm:text-4xl font-black leading-tight text-white mb-3 sm:mb-4 whitespace-pre-line tracking-tight">
                {slide.title}
              </h2>

              <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-sm">
                {slide.description}
              </p>

              <ul className="space-y-2.5">
                {slide.bullets.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-white/70"
                    style={{
                      transitionDelay: `${i * 60}ms`,
                      opacity: animating ? 0 : 1,
                      transition: 'opacity 0.3s',
                    }}
                  >
                    <span
                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${slide.accent}20`, border: `1px solid ${slide.accent}40` }}
                    >
                      <svg width="7" height="7" viewBox="0 0 8 8" fill="none">
                        <path d="M1.5 4L3.5 6L6.5 2" stroke={slide.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — live preview (hidden on mobile) */}
            <div className="relative hidden lg:flex items-center justify-center px-6 py-8 lg:py-6">
              {/* Subtle bg tint */}
              <div
                className="absolute inset-0 opacity-5"
                style={{ background: `radial-gradient(ellipse at 60% 40%, ${slide.accent}, transparent 70%)` }}
              />
              <div
                className="relative w-full max-w-sm bg-[#0c1526] border border-white/[0.07] rounded-2xl p-4 transition-all duration-280 shadow-2xl"
                style={{
                  opacity: animating ? 0 : 1,
                  transform: animating ? `translateX(${direction * 30}px)` : 'translateX(0)',
                  minHeight: 280,
                }}
              >
                {/* Fake browser chrome */}
                <div className="flex items-center gap-1.5 mb-4 pb-3 border-b border-white/[0.06]">
                  <div className="w-2 h-2 rounded-full bg-red-500/40"/>
                  <div className="w-2 h-2 rounded-full bg-yellow-500/40"/>
                  <div className="w-2 h-2 rounded-full bg-green-500/40"/>
                  <div className="flex-1 mx-2 h-4 bg-white/[0.04] rounded-full flex items-center px-2">
                    <span className="text-[8px] text-white/20">playeriq.app/{slide.id}</span>
                  </div>
                </div>
                {slide.preview}
              </div>
            </div>
          </div>

          {/* Bottom nav */}
          <div className="flex items-center justify-between px-5 sm:px-8 py-4 sm:py-5 border-t border-white/[0.05] flex-shrink-0">
            <button
              onClick={prev}
              disabled={current === 0}
              className="flex items-center gap-2 text-sm font-medium text-white/40 hover:text-white disabled:opacity-0 disabled:pointer-events-none transition-all"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back
            </button>

            <span className="text-[11px] text-white/20 font-medium">
              {current + 1} / {slides.length}
            </span>

            {current < slides.length - 1 ? (
              <button
                onClick={next}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white transition-all"
                style={{ background: slide.accent, boxShadow: `0 4px 20px ${slide.accent}40` }}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ) : (
              <button
                onClick={handleClose}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white transition-all"
                style={{ background: slide.accent, boxShadow: `0 4px 20px ${slide.accent}40` }}
              >
                Get Started
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksModal;
